import { useProfile } from "@/hooks/useProfile";
import { CreatorStudent } from "@/hooks/useCreatorStudents";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { StudentPagination } from "@/components/students/StudentPagination";
import { Users } from "lucide-react";
import { useState, useEffect } from "react";

interface StudentTableProps {
  students: CreatorStudent[];
  isLoading: boolean;
  openStudentDetail: (id: string) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  totalStudents: number;
}

const StudentTable = ({
  students,
  isLoading,
  openStudentDetail,
  currentPage,
  onPageChange,
  pageSize = 10,
  totalStudents
}: StudentTableProps) => {
  useEffect(() => {
    console.log("StudentTable - Received students:", students);
    if (students && students.length > 0) {
      console.log("StudentTable - Sample student:", students[0]);
      console.log("StudentTable - Total students received:", students.length);
      
      // Check if creator is included in students
      const uniqueStudentIds = new Set(students.map(s => s.student_id));
      console.log("StudentTable - Unique student IDs:", uniqueStudentIds.size);
      
      // Log each student with key data points
      students.forEach((student, index) => {
        console.log(`Student ${index + 1}:`, {
          id: student.student_id,
          name: student.student_name || "N/A",
          email: student.student_email,
          course: student.course_title,
          progress: student.progress_percent,
          revenue: student.total_revenue
        });
      });
    }
  }, [students]);

  useEffect(() => {
    console.log("StudentTable - Selection state:", {
      selectedStudents: [],
      totalSelected: 0
    });
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Enrolled</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Last Active</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array(3).fill(0).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={8}>
                    <Skeleton className="h-10 w-full" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  if (!students || students.length === 0) {
    return (
      <div className="text-center py-8 border rounded-md bg-muted/5">
        <Users className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-muted-foreground">No students enrolled yet</p>
        <p className="text-sm text-muted-foreground">Students will appear here once they enroll in your courses</p>
      </div>
    );
  }
  
  const totalPages = Math.ceil(totalStudents / pageSize);

  console.log("StudentTable - Render state:", {
    displayedStudents: students.length,
    totalStudents,
    currentPage,
    totalPages
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Course</TableHead>
              <TableHead>Enrolled</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Last Active</TableHead>
              <TableHead>Revenue</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((student) => {
              console.log(`Rendering student row: ${student.student_id}, ${student.student_name}`);
              return (
                <TableRow key={`${student.student_id}-${student.course_id}`}>
                  <TableCell>{student.student_name || "N/A"}</TableCell>
                  <TableCell>{student.student_email}</TableCell>
                  <TableCell>{student.course_title}</TableCell>
                  <TableCell>{new Date(student.enrolled_at).toLocaleDateString()}</TableCell>
                  <TableCell>{student.progress_percent}%</TableCell>
                  <TableCell>{student.last_active ? new Date(student.last_active).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>${student.total_revenue.toFixed(2)}</TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openStudentDetail(student.student_id)}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      
      <StudentPagination
        page={currentPage}
        pageSize={pageSize}
        totalPages={totalPages}
        totalItems={totalStudents}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default StudentTable;
